import { useState } from "react";

export interface FilterState {
  search: string;
  category: string;
  factory: string;
  commissionMin: number;
  commissionMax: number;
  priceMin: string;
  priceMax: string;
  warehouse: string;
  sortBy: string;
}

interface Props {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  resultCount: number;
}

const FACTORIES = ["All Factories", "TechVision Factory", "PureGlow Labs", "FitForge Manufacturing", "GreenThread Mills", "LightCraft Industries", "HydroForge Co.", "SoundWave Factory", "EcoKitchen Works"];
const WAREHOUSES = ["All Locations", "USA Local", "Overseas"];
const SORT_OPTIONS = [
  { value: "trending", label: "Trending" },
  { value: "commission_high", label: "Highest Commission" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "rating", label: "Top Rated" },
];

export default function MarketplaceFilters({ filters, onChange, resultCount }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = (partial: Partial<FilterState>) => onChange({ ...filters, ...partial });

  const hasActiveFilters =
    filters.search !== "" ||
    filters.factory !== "All Factories" ||
    filters.warehouse !== "All Locations" ||
    filters.priceMin !== "" ||
    filters.priceMax !== "" ||
    filters.commissionMin > 0;

  const clearAll = () => onChange({
    search: "",
    category: filters.category,
    factory: "All Factories",
    commissionMin: 0,
    commissionMax: 100,
    priceMin: "",
    priceMax: "",
    warehouse: "All Locations",
    sortBy: "trending",
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
      {/* Search Bar */}
      <div className="relative mb-4">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          placeholder="Search products, brands, categories..."
          className="w-full pl-11 pr-12 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors"
        />
        {filters.search && (
          <button onClick={() => update({ search: "" })} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        )}
      </div>

      {/* Quick Filters Row */}
      <div className="flex flex-wrap gap-3 mb-3">
        {/* Factory Filter */}
        <div className="flex items-center gap-2">
          <i className="ri-building-line text-gray-400 text-sm" />
          <select
            value={filters.factory}
            onChange={(e) => update({ factory: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white cursor-pointer outline-none focus:border-orange-400 transition-colors"
          >
            {FACTORIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        {/* Warehouse Filter */}
        <div className="flex items-center gap-2">
          <i className="ri-map-pin-line text-gray-400 text-sm" />
          <select
            value={filters.warehouse}
            onChange={(e) => update({ warehouse: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white cursor-pointer outline-none focus:border-orange-400 transition-colors"
          >
            {WAREHOUSES.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <i className="ri-sort-desc text-gray-400 text-sm" />
          <select
            value={filters.sortBy}
            onChange={(e) => update({ sortBy: e.target.value })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white cursor-pointer outline-none focus:border-orange-400 transition-colors"
          >
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Advanced Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${showAdvanced ? "bg-orange-500 text-white" : "border border-gray-200 text-gray-600 hover:border-orange-300"}`}
        >
          <i className="ri-equalizer-line" /> Advanced {showAdvanced ? "▲" : "▼"}
        </button>

        {hasActiveFilters && (
          <button onClick={clearAll} className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-red-500 border border-red-100 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-close-circle-line" /> Clear All
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Commission Range */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              Min Commission: <span className="text-orange-500">{filters.commissionMin}%</span>
            </label>
            <input
              type="range"
              min={0}
              max={30}
              value={filters.commissionMin}
              onChange={(e) => update({ commissionMin: Number(e.target.value) })}
              className="w-full accent-orange-500 cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0%</span><span>30%</span>
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Price Range</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={filters.priceMin}
                onChange={(e) => update({ priceMin: e.target.value })}
                placeholder="Min $"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-orange-400 transition-colors"
              />
              <span className="text-gray-400 text-xs">—</span>
              <input
                type="number"
                value={filters.priceMax}
                onChange={(e) => update({ priceMax: e.target.value })}
                placeholder="Max $"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-orange-400 transition-colors"
              />
            </div>
          </div>

          {/* Quick Commission Presets */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Commission Presets</label>
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "Any", min: 0 },
                { label: "5%+", min: 5 },
                { label: "10%+", min: 10 },
                { label: "15%+", min: 15 },
              ].map((p) => (
                <button
                  key={p.label}
                  onClick={() => update({ commissionMin: p.min })}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${filters.commissionMin === p.min ? "bg-orange-500 text-white" : "border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          <span className="font-semibold text-gray-900">{resultCount}</span> products found
          {hasActiveFilters && <span className="text-orange-500 ml-1">(filtered)</span>}
        </p>
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-1.5">
            {filters.search && (
              <span className="flex items-center gap-1 bg-orange-50 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                &quot;{filters.search}&quot;
                <button onClick={() => update({ search: "" })} className="cursor-pointer hover:text-orange-800"><i className="ri-close-line text-xs" /></button>
              </span>
            )}
            {filters.factory !== "All Factories" && (
              <span className="flex items-center gap-1 bg-orange-50 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                {filters.factory}
                <button onClick={() => update({ factory: "All Factories" })} className="cursor-pointer hover:text-orange-800"><i className="ri-close-line text-xs" /></button>
              </span>
            )}
            {filters.warehouse !== "All Locations" && (
              <span className="flex items-center gap-1 bg-orange-50 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                {filters.warehouse}
                <button onClick={() => update({ warehouse: "All Locations" })} className="cursor-pointer hover:text-orange-800"><i className="ri-close-line text-xs" /></button>
              </span>
            )}
            {filters.commissionMin > 0 && (
              <span className="flex items-center gap-1 bg-orange-50 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                {filters.commissionMin}%+ commission
                <button onClick={() => update({ commissionMin: 0 })} className="cursor-pointer hover:text-orange-800"><i className="ri-close-line text-xs" /></button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
